/**
 * State-card thumbnail freshness + posed capture — the shared core behind every
 * host's state-card strip (sandbox PoseStrip, TraceFrontend states panel).
 *
 * A state card depicts the owner's WHOLE subtree posed at one state (or Base).
 * Whether a cached card is still accurate is a pure function of authored data:
 * the subtree's authored appearance (Base) plus the per-state overrides scattered
 * across the subtree (a state's deltas can live on ANY node — most importantly a
 * DESCENDANT, e.g. editing a child's `color` while the state is active writes
 * `child.states[name].values`). This module owns:
 *
 *  - the signature/hash trio (`baseSubtreeSig`, `stateOverrideSig`,
 *    `stateCardHash`) — pure reads over SceneObject/Properties/Renderer/Text data,
 *    no cache, no Babylon render;
 *  - `captureStateCard` — the posed subtree capture (per-object prior-state
 *    snapshot → `applyStateToSubtree` → per-object restore) around the shared
 *    `captureObjectThumbnail` rig;
 *  - `enqueueStateCardCapture` — a single-flight, key-deduped capture queue.
 *
 * Caches stay PER-HOST (sandbox ThumbnailCache map, TF zustand slice) — hosts
 * store `{ dataUrl|file, hash }` per card and pass an `isFresh` check into the
 * queue. Keys are host-owned too; the queue dedups on transform uniqueId.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { StateDefinition } from '../../scenedata/behaviors/propertiesdata';
import { Properties } from '../../behaviors/properties';
import { Renderer } from '../../behaviors/renderer';
import { Text } from '../../behaviors/text';
import { AnimationSequence } from '../../behaviors/animationsequence';
import { captureObjectThumbnail } from '../ThumbnailCapture';
import { applyStateToSubtree } from './statesActions';
import { groupDescendants } from './groupBoundaries';

// --- Animation-aware capture gating ----------------------------------------
//
// A state's animation auto-plays a live PREVIEW on apply (state-bound clips),
// and the animation root's transforms are driven OUTSIDE the state system — so
// `applyStateToSubtree` in a pose window does not neutralize them. Capturing
// while a sequence is mid-motion bakes the mid-flight frame into the card
// (e.g. the invisible start of an enter animation → a blank thumbnail). The
// card depicts the WHOLE subtree, so these checks walk owner + descendants —
// a group whose CHILD carries the animation is just as polluted as one whose
// owner does. (Hoisted from TraceFrontend, which had the policy right but only
// checked the owner's own sequence.)

/** Every AnimationSequence across the owner's captured subtree. */
function subtreeAnimationSequences(owner: SceneObject): AnimationSequence[] {
    const seqs: AnimationSequence[] = [];
    for (const o of [owner, ...owner.getDescendants()]) {
        const seq = o.getBehavior(AnimationSequence);
        if (seq) seqs.push(seq);
    }
    return seqs;
}

/**
 * True while any sequence in the subtree is mid enter/exit. These play ONCE and
 * settle back to idle/main, so the right move is to WAIT (defer the capture)
 * rather than snap — stopping them would also kill the one-shot preview the
 * user is watching in the live viewport. The shared queue skips (without
 * storing) while this is true; hosts re-arm on their own cadence.
 */
export function subtreeAnimationSettling(owner: SceneObject): boolean {
    return subtreeAnimationSequences(owner).some((s) => {
        const state = s.getCurrentState();
        return state === 'enter' || state === 'exit';
    });
}

/**
 * True while any property or transform TRANSITION is mid-flight anywhere in the
 * subtree — an animated state switch is still blending toward its target.
 *
 * For hosts that capture the LIVE pose (current-pose active-card captures, e.g.
 * TF's `captureActiveStateThumbnail`): defer while this is true, or the card
 * bakes the mid-blend frame. The shared posed QUEUE waits it out too — not for
 * image correctness (a posed capture renders the card's target pose directly),
 * but because the pose window MUTATES the live node: an instant apply/restore
 * landing mid-tween cancels the user's switch transition and snaps the pose to
 * its target (states jump instead of easing — the switch-reassert-stomp class).
 */
export function subtreeTransitionSettling(owner: SceneObject): boolean {
    for (const o of [owner, ...owner.getDescendants()]) {
        if (o.isTransformTransitioning) return true;
        const p = o.getBehavior(Properties);
        if (p && p.activeTransitionCount > 0) return true;
    }
    return false;
}

/**
 * Pose hook that snaps every mid-`main` (while-active, looping) sequence in the
 * subtree to its rest pose for the off-screen render, then resumes the loop —
 * the "frame 0" capture for while-active animations. Returns `undefined` when
 * nothing is looping (no pose needed). Unlike enter/exit, a loop never settles,
 * so waiting is not an option.
 *
 * KNOWN ARTIFACT: `play()` restarts the loop from frame 0 (the handle API has
 * no frame-seek), so a capture during a live loop visibly resets its phase.
 * Freshness gating keeps this to one capture per real content change (though
 * captureObjectThumbnail invokes the pose hook at least TWICE per capture —
 * measure + render, plus a retry per frame its posed meshes are not ready — so
 * a single capture does two or more stop/resume cycles); a phase-exact resume
 * needs seek plumbing through AnimationSequence first.
 */
export function restLoopingAnimationsPose(owner: SceneObject): (() => () => void) | undefined {
    const ignore = () => {};
    const looping = subtreeAnimationSequences(owner).filter((s) => s.getCurrentState() === 'main');
    if (looping.length === 0) return undefined;
    return () => {
        for (const s of looping) {
            try { s.stop(); } catch { ignore(); }
        }
        return () => {
            for (const s of looping) {
                try { void s.getMain().play(); } catch { ignore(); }
            }
        };
    };
}

/**
 * Hash of the owner subtree's authored (state-independent) appearance — the
 * imagery inputs of the Base card, and (via base-inheritance: a state is a diff
 * from Base) of every state card too, so a Base edit invalidates all cards.
 *
 * Per node this covers:
 *  - the authored transform (sceneData — states pose the live node, they don't
 *    rewrite authored transform data);
 *  - the authored active flag (sceneData — states show and hide through a
 *    runtime override, they don't rewrite authored active either);
 *  - the authored property declarations INCLUDING values (color/material pins
 *    route through declaration values, so bound-slot edits land here);
 *  - the Renderer materials array — an UNBOUND material slot edit (a material
 *    applied before any state binding exists) changes appearance without touching
 *    any declaration value, so it must be hashed directly;
 *  - the authored Text data (content, font, size, color, layout). A literal text
 *    edit touches no declaration either. Authored, not resolved: a `$ref` text
 *    stays the ref here, and its value is already in the declarations/overrides,
 *    so a state switch doesn't move the Base signature.
 */
export function baseSubtreeSig(owner: SceneObject): string {
    const parts: string[] = [];
    const node = (o: SceneObject) => {
        const data = o.getSceneObjectData();
        const t = data.transform;
        const p = o.getBehavior(Properties);
        const active = data.active ?? true;
        parts.push(
            o.name,
            JSON.stringify(t?.translation ?? 0),
            JSON.stringify(t?.rotation ?? 0),
            JSON.stringify(t?.scale ?? 0),
            `a:${JSON.stringify(active)}`,
        );
        if (p) parts.push(JSON.stringify(p.getData().properties ?? []));
        const r = o.getBehavior(Renderer);
        if (r) parts.push(JSON.stringify(r.getData().materials ?? []));
        const text = o.getBehavior(Text);
        if (text) parts.push(JSON.stringify(text.getData()));
    };
    node(owner);
    for (const d of owner.getDescendants()) node(d);
    return parts.join('|');
}

/**
 * The VISUAL fields of a state definition — what the posed card actually shows.
 * `transition` is excluded (captures snap instantly; duration/easing never change
 * the imagery) and so is `enterFrom` (it only seeds the *animated* entry offset,
 * which an instant capture overwrites). Hashing the whole def would restale — and
 * recapture — every card on a transition tweak.
 */
function visualDefSig(def: StateDefinition): string {
    return JSON.stringify({ values: def.values, transform: def.transform, active: def.active });
}

/**
 * Hash of every subtree node's `states[name]` override (value/transform/active
 * deltas) for a given state. A state's deltas can live on ANY node in the subtree
 * — most importantly on a DESCENDANT. The owner's own state def covers only the
 * owner, so without the walk a descendant-only override leaves the card hash
 * unchanged → the card is treated as fresh and never recaptured (the
 * descendant-override staleness class).
 *
 * BOUNDARY-PRUNED: a nested boundary's per-state overrides belong to the INNER
 * group — the outer card's pose window (`applyStateToSubtree`) never applies
 * them, so hashing them would restale (and pointlessly recapture) outer cards on
 * inner-group edits, incl. when the two groups share a default name ("State 1").
 * `baseSubtreeSig` deliberately stays full-subtree: the IMAGE includes nested
 * content, so an authored edit below a boundary must restale every card.
 */
export function stateOverrideSig(owner: SceneObject, name: string): string {
    const parts: string[] = [];
    const node = (o: SceneObject) => {
        const def = o.getBehavior(Properties)?.getData().states?.[name];
        if (def) parts.push(o.name, visualDefSig(def));
    };
    node(owner);
    for (const d of groupDescendants(owner)) node(d);
    return parts.join('|');
}

/**
 * Content hash for a state card. `null` = the Base card (base signature only);
 * a named state folds its override signature in FRONT of the base signature
 * (base-inheritance: unmentioned facets render at Base, so a Base edit restales
 * every card).
 */
export function stateCardHash(owner: SceneObject, name: string | null): string {
    const base = baseSubtreeSig(owner);
    if (name === null) return `base:${base}`;
    return `st:${stateOverrideSig(owner, name)}|${base}`;
}

/** A captured card image plus the content hash it depicts. */
export interface StateCardCapture {
    file: File;
    /**
     * The card's content hash, computed synchronously BEFORE the async capture —
     * it must describe what was captured, not whatever the scene became while the
     * RTT settled. Store it alongside the image and compare with `stateCardHash`
     * to decide freshness.
     */
    hash: string;
}

/**
 * Capture one state card: pose the owner's WHOLE subtree at `name` (`null` =
 * Base), capture via the shared isolated-RTT rig, and restore each object's
 * PRIOR active state.
 *
 * We cannot skip posing when "the owner is already at `name`": a DESCENDANT may
 * be in a different state than the owner (e.g. a child the user just moved sits
 * in State 1 while the owner is at Base), so capturing the live pose for the
 * Base card would bake that descendant's State-1 pose into the Base thumbnail.
 * Snapshot + restore per object covers this — restoring the OWNER's prior state
 * across the subtree would not.
 *
 * Returns `null` when the capture rig can't produce an image (meshless subtree,
 * NullEngine, RTT failure) — hosts keep showing the placeholder.
 */
export async function captureStateCard(
    owner: SceneObject,
    name: string | null,
    opts?: { size?: number },
): Promise<StateCardCapture | null> {
    const ignore = () => {};
    const pose = () => {
        const subtree = [owner, ...owner.getDescendants()];
        // Record the prior editor pick of exactly the objects the pose will touch —
        // Base poses every Properties carrier; a named card only objects that
        // carry the state. The restore undoes the pose's own mutation and
        // nothing else: instant-re-asserting an object the pose never changed
        // would gratuitously recompute (and snap) it. The pose window itself is
        // SYNCHRONOUS (captureTransformSubtree does apply → rtt.render() →
        // restore with no await between), so nothing can change state under us
        // inside the window.
        const prior = new Map<SceneObject, string | null | undefined>();
        for (const o of subtree) {
            const p = o.getBehavior(Properties);
            if (!p) continue;
            if (name !== null && !p.getStateNames().includes(name)) continue;
            prior.set(o, p.getEditorState());
        }
        try { applyStateToSubtree(owner, name ?? undefined); } catch { ignore(); }
        // Snap any while-active loop to rest INSIDE the pose window, so the
        // animation root's mid-flight transforms don't leak into the card.
        const restoreLoops = restLoopingAnimationsPose(owner)?.();
        return () => {
            // Restore each touched object to the exact pick it had before (NOT
            // just the owner's) — descendants can differ. States first, loops
            // after, so the resumed loop plays against the restored state.
            for (const [o, state] of prior) {
                try { o.getBehavior(Properties)?.setEditorState(state, true); } catch { ignore(); }
            }
            restoreLoops?.();
        };
    };

    const hash = stateCardHash(owner, name);
    const file = await captureObjectThumbnail(owner, { size: opts?.size, pose });
    if (!file) return null;
    return { file, hash };
}

// --- Serialized capture queue ----------------------------------------------
//
// CRITICAL: captureObjectThumbnail creates a temporary camera + RTT per call.
// Running many in parallel leaves multiple thumbnail cameras alive in the scene
// at once (each visible in the explorer until its async capture settles). We
// therefore run captures ONE AT A TIME through a single queue, with a queued-key
// set so the same card is never queued twice. This applies to every host — it
// mirrors the keyframe path's serialized fill loop (which is why keyframes never
// leaked cameras).
//
// The queue is module-level, so it is PAGE-GLOBAL: shared across every
// SceneObject from every scene / TraceViewer instance on the page, not scoped
// per-scene. That is intentional (one thumbnail camera alive at a time,
// page-wide); queueKey keys on the session-unique `uniqueId` so entries from
// different scenes never collide.

/**
 * A generic posed-card job for the shared serialized queue. States are one
 * producer (enqueueStateCardCapture); the sandbox Pages strip is another (its
 * pose shows one page instead of applying a state). `computeHash` re-runs at
 * drain time for the freshness re-check; `capture` produces the image + the
 * hash it depicts (or null when the rig can't render).
 */
export interface PosedCardJob {
    /** The object whose subtree the card depicts — also the settling guard target. */
    owner: SceneObject;
    /** Queue identity — producers key on session-unique transform uniqueIds. */
    key: string;
    computeHash: () => string;
    isFresh: (currentHash: string) => boolean;
    capture: () => Promise<StateCardCapture | null>;
    onCaptured: (capture: StateCardCapture) => void | Promise<void>;
}

const queued = new Set<string>(); // queue keys currently queued or in flight
let draining = false;
const pending: PosedCardJob[] = [];

/** Queue identity for a card — transform uniqueIds are session-unique. */
function queueKey(owner: SceneObject, name: string | null): string {
    return `${owner.transform.uniqueId}:${name ?? '__base__'}`;
}

async function drain(): Promise<void> {
    if (draining) return;
    draining = true;
    try {
        let job: PosedCardJob | undefined;
        while ((job = pending.shift())) {
            const key = job.key;
            try {
                // Re-check freshness at capture start (not just at enqueue): an
                // edit (or an undo) between enqueue and drain may have made the
                // card fresh again — capturing then would bake the reverted look
                // under the new hash.
                // Defer while a play-once enter/exit preview is settling: the
                // animation root is mid-blend and would bake a mid-flight frame.
                // Skipping RELEASES the key without storing, so the card stays
                // stale and the host's next refresh/enqueue retries after settle.
                //
                // WAIT OUT in-flight property/transform TRANSITIONS before posing.
                // The rendered image would be transition-independent (the pose
                // window renders the card's target pose directly), but the pose
                // itself is NOT: it instant-applies a state to the LIVE node and
                // instant-restores after, which CANCELS a user's in-flight switch
                // tween and snaps the pose onto its target — states jump instead
                // of easing (the switch-reassert-stomp class; see the engine
                // contract test switch-reassert-stomp.test.ts). Time transitions
                // are bounded (default 0.3s), so poll briefly; if something is
                // still unsettled at the cap, SKIP like the animation case — the
                // card stays stale and the host's next enqueue retries.
                let waitedMs = 0;
                while (subtreeTransitionSettling(job.owner) && waitedMs < 2000) {
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    waitedMs += 100;
                }
                if (!job.isFresh(job.computeHash())
                    && !subtreeAnimationSettling(job.owner)
                    && !subtreeTransitionSettling(job.owner)) {
                    const capture = await job.capture();
                    // Await the host's store step (e.g. File → dataURL) so the
                    // queue key is only released once the cache reflects the
                    // capture — otherwise a getThumbnail landing in the gap sees
                    // "stale + not queued" and double-captures the card.
                    if (capture) await job.onCaptured(capture);
                }
            } catch { /* skip this card; keep draining */ }
            queued.delete(key);
        }
    } finally {
        draining = false;
    }
}

/**
 * Enqueue a state-card capture — deduped (a card already queued or in flight is
 * not queued again; the later call's `onCaptured` never fires) and serialized
 * (one capture at a time, see the camera-leak note above).
 *
 * `isFresh` is the host's cache check (stored hash === `currentHash`); it runs
 * at enqueue AND again when the job reaches the front of the queue. `onCaptured`
 * delivers the image + the hash it depicts for the host to store; it may return
 * a promise (e.g. File → dataURL conversion), which is awaited before the card's
 * queue slot is released.
 */
export function enqueueStateCardCapture(
    owner: SceneObject,
    name: string | null,
    opts: {
        size?: number;
        isFresh: (currentHash: string) => boolean;
        onCaptured: (capture: StateCardCapture) => void | Promise<void>;
    },
): void {
    enqueuePosedCardCapture({
        owner,
        key: queueKey(owner, name),
        computeHash: () => stateCardHash(owner, name),
        isFresh: opts.isFresh,
        capture: () => captureStateCard(owner, name, { size: opts.size }),
        onCaptured: opts.onCaptured,
    });
}

/**
 * Enqueue a GENERIC posed-card capture through the same serialized, deduped
 * queue (see the camera-leak note above — one thumbnail camera page-wide).
 * Producers supply the pose via `capture` (typically `captureObjectThumbnail`
 * with a pose hook) and the freshness pair (`computeHash` + `isFresh`); the
 * queue contributes dedup, serialization, and the animation/transition
 * settling guards around `owner`'s subtree.
 */
export function enqueuePosedCardCapture(job: PosedCardJob): void {
    if (job.isFresh(job.computeHash())) return;
    if (queued.has(job.key)) return;
    queued.add(job.key);
    pending.push(job);
    void drain();
}
